import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LOCALE_NAMES, LOCALES, type Locale } from '@barev/i18n';
import { Button, Card, Chip, Field, Icon, Segmented } from '@barev/ui';
import { errorText, haptic, useI18n, useLocation, useToast, useTRPC } from '@barev/web';
import { useState } from 'react';
import { ClubTicket, LEVEL_NAME, type Level } from '../lib.tsx';

export function Profile() {
  const { t, dt } = useI18n();
  const trpc = useTRPC();
  const me = useQuery(trpc.client.me.queryOptions());
  const { query } = useLocation();
  const level = (me.data?.membership.level ?? 'BAREV') as Level;
  const plus = level !== 'BAREV';

  return (
    <main className="app app--hero">
      <section className="hero">
        <h1 className="hero__hello">
          <small>{t('client.profile.title')}</small>
          <span>{me.data?.firstName}</span>
        </h1>
        <p className="hero__note">{me.data?.agencyName ? t('client.profile.fromAgency', { agency: me.data.agencyName }) : t('client.profile.noAgency')}</p>
        <ClubTicket
          level={level}
          code={me.data?.cardCode}
          href="#/membership"
          until={plus && me.data?.membership.endsAt ? t('client.home.levelUntil', { plan: LEVEL_NAME[level], date: dt(me.data.membership.endsAt, { day: 'numeric', month: 'long', year: 'numeric' }) }) : t('client.home.free')}
          action={
            <>
              <Icon name="star" size={26} />
              <span>{t('client.card.plans')}</span>
            </>
          }
        />
      </section>

      <SegmentSwitch current={me.data?.segment ?? null} />

      {!me.data?.agencyName && <AgencyCode autoFocus={query.get('code') === '1'} />}

      <LanguagePicker />

      <nav className="links">
        <a href="#/points">
          <Icon name="star" size={20} />
          <span>
            {t('client.points.title')}
            {me.data ? <b style={{ marginLeft: 8 }}>{new Intl.NumberFormat('ru-RU').format(me.data.points)}</b> : null}
          </span>
          <Icon name="arrow" size={18} />
        </a>
        <a href="#/orders">
          <Icon name="clock" size={20} />
          <span>{t('client.orders.title')}</span>
          <Icon name="arrow" size={18} />
        </a>
        <a href="#/perks">
          <Icon name="gift" size={20} />
          <span>{t('client.card.title')}</span>
          <Icon name="arrow" size={18} />
        </a>
        <a href="#/chat">
          <Icon name="chat" size={20} />
          <span>{t('client.nav.chat')}</span>
          <Icon name="arrow" size={18} />
        </a>
      </nav>
    </main>
  );
}

/** ONB-6: ручной ввод кода агентства */
function AgencyCode({ autoFocus }: { autoFocus?: boolean }) {
  const { t } = useI18n();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const [code, setCode] = useState('');
  const apply = useMutation(
    trpc.client.applyAgencyCode.mutationOptions({
      onSuccess: (r) => {
        haptic.ok();
        toast(r.gift ? t('client.profile.codeGift', { agency: r.agencyName }) : t('client.profile.codeAccepted', { agency: r.agencyName }));
        void qc.invalidateQueries();
      },
      onError: (e) => {
        haptic.error();
        toast(errorText(t, e), 'error');
      },
    }),
  );
  return (
    <Card tone="home" className="bc-stack">
      <h2 className="bc-h3">{t('client.profile.codeTitle')}</h2>
      <p className="bc-small" style={{ margin: 0 }}>
        {t('client.profile.codeText')}
      </p>
      <form
        className="bc-row"
        onSubmit={(e) => {
          e.preventDefault();
          if (code.trim()) apply.mutate({ code: code.trim() });
        }}
      >
        <Field>
          <input className="bc-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t('client.profile.codePlaceholder')} autoFocus={autoFocus} maxLength={32} autoCapitalize="characters" aria-label={t('client.profile.codeTitle')} />
        </Field>
        <Button type="submit" disabled={apply.isPending || code.trim().length < 2}>
          {t('common.apply')}
        </Button>
      </form>
    </Card>
  );
}

export function LanguagePicker() {
  const { t, locale, setLocale } = useI18n();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const save = useMutation(trpc.client.setLocale.mutationOptions({ onSuccess: () => qc.invalidateQueries() }));
  const pick = (l: Locale) => {
    setLocale(l);
    save.mutate({ locale: l });
  };
  return (
    <Card className="bc-stack">
      <h2 className="bc-h3 bc-row">
        <Icon name="globe" size={20} />
        {t('client.profile.language')}
      </h2>
      <div className="bc-chips">
        {LOCALES.map((l) => (
          <Chip key={l} pressed={l === locale} onClick={() => pick(l)} lang={l}>
            {LOCALE_NAMES[l]}
          </Chip>
        ))}
      </div>
    </Card>
  );
}

/** Сценарий приложения: переезжаю / уже живу — меняет главную и меню */
function SegmentSwitch({ current }: { current: 'MOVING' | 'LIVING' | null }) {
  const { t } = useI18n();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const set = useMutation(
    trpc.client.setSegment.mutationOptions({
      onSuccess: () => {
        haptic.ok();
        void qc.invalidateQueries();
      },
      onError: (e) => toast(errorText(t, e), 'error'),
    }),
  );
  return (
    <Card className="bc-stack">
      <h2 className="bc-h3">{t('client.segment.profileTitle')}</h2>
      <Segmented
        value={current ?? 'LIVING'}
        onChange={(segment) => set.mutate({ segment })}
        options={[
          { value: 'MOVING', label: t('client.segment.moving') },
          { value: 'LIVING', label: t('client.segment.living') },
        ]}
      />
    </Card>
  );
}
